import { useState } from 'react'
import { useFinancialPlanStore, FamilyMember } from '../../store/financialPlanStore'
import { Plus, Trash2, User, Users } from 'lucide-react'

export function PersonalInfoStep() {
  const {
    currentPlan,
    addFamilyMember,
    updateFamilyMember,
    removeFamilyMember,
  } = useFinancialPlanStore()
  
  const [showAddForm, setShowAddForm] = useState(false)
  const [newMember, setNewMember] = useState({
    name: '',
    dateOfBirth: '',
    relationship: 'spouse' as FamilyMember['relationship'],
    retirementAge: 65,
  })
  
  const handleAddMember = () => {
    if (newMember.name && newMember.dateOfBirth) {
      addFamilyMember(newMember)
      setNewMember({
        name: '',
        dateOfBirth: '',
        relationship: 'spouse',
        retirementAge: 65,
      })
      setShowAddForm(false)
    }
  }
  
  const calculateAge = (dob: string) => {
    const birth = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birth.getFullYear()
    const m = today.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--
    }
    return age
  }
  
  // Ensure client exists
  const client = currentPlan?.familyMembers.find(m => m.relationship === 'client')
  
  if (!currentPlan) return null
  
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <User className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-900 mb-1">
              About This Section
            </h3>
            <p className="text-sm text-blue-700">
              Add your personal information and family members. This helps us create
              accurate projections for your financial plan, including retirement ages
              and life events.
            </p>
          </div>
        </div>
      </div>
      
      {/* Client (Primary) Information */}
      {!client ? (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg flex items-center">
            <User className="h-5 w-5 mr-2 text-primary-600" />
            Primary Client Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name *</label>
              <input
                type="text"
                value={currentPlan.clientName}
                readOnly
                className="input-field bg-gray-50"
              />
            </div>
            
            <div>
              <label className="label">Date of Birth *</label>
              <input
                type="date"
                onChange={(e) => {
                  addFamilyMember({
                    name: currentPlan.clientName,
                    dateOfBirth: e.target.value,
                    relationship: 'client',
                    retirementAge: 65,
                  })
                }}
                className="input-field"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">
                Planned Retirement Age
                <span className="text-xs text-gray-500 ml-2">
                  (typically 65-67)
                </span>
              </label>
              <input
                type="number"
                defaultValue={65}
                min="50"
                max="80"
                className="input-field"
                onChange={(e) => {
                  const clientMember = currentPlan.familyMembers.find(m => m.relationship === 'client')
                  if (clientMember) {
                    updateFamilyMember(clientMember.id, {
                      retirementAge: parseInt(e.target.value)
                    })
                  }
                }}
              />
            </div>
            
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                value={currentPlan.email}
                readOnly
                className="input-field bg-gray-50"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center">
                <User className="h-5 w-5 mr-2 text-primary-600" />
                <h4 className="font-semibold">{client.name}</h4>
                <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                  Primary Client
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Age: {calculateAge(client.dateOfBirth)} | Retirement Age: {client.retirementAge || 65}
              </p>
            </div>
            <button
              onClick={() => removeFamilyMember(client.id)}
              className="text-red-600 hover:text-red-700 text-sm"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Family Members */}
      <div className="space-y-4 mt-8">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg flex items-center">
            <Users className="h-5 w-5 mr-2 text-primary-600" />
            Family Members
          </h3>
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary text-sm flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Family Member
            </button>
          )}
        </div>
        
        {/* Existing Family Members */}
        {currentPlan.familyMembers
          .filter(m => m.relationship !== 'client')
          .map(member => (
            <div
              key={member.id}
              className="border border-gray-200 rounded-lg p-4 bg-white"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{member.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {member.relationship.charAt(0).toUpperCase() + member.relationship.slice(1)} |
                    Age: {calculateAge(member.dateOfBirth)}
                    {member.retirementAge && ` | Retirement Age: ${member.retirementAge}`}
                  </p>
                </div>
                <button
                  onClick={() => removeFamilyMember(member.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        
        {/* Add New Member Form */}
        {showAddForm && (
          <div className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50">
            <h4 className="font-medium mb-4">Add Family Member</h4>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Name *</label>
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    className="input-field"
                    placeholder="Jane Smith"
                  />
                </div>
                
                <div>
                  <label className="label">Date of Birth *</label>
                  <input
                    type="date"
                    value={newMember.dateOfBirth}
                    onChange={(e) => setNewMember({ ...newMember, dateOfBirth: e.target.value })}
                    className="input-field"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Relationship *</label>
                  <select
                    value={newMember.relationship}
                    onChange={(e) => setNewMember({
                      ...newMember,
                      relationship: e.target.value as FamilyMember['relationship']
                    })}
                    className="input-field"
                  >
                    <option value="spouse">Spouse/Partner</option>
                    <option value="child">Child</option>
                    <option value="dependent">Other Dependent</option>
                  </select>
                </div>
                
                {newMember.relationship === 'spouse' && (
                  <div>
                    <label className="label">Retirement Age</label>
                    <input
                      type="number"
                      value={newMember.retirementAge}
                      onChange={(e) => setNewMember({
                        ...newMember,
                        retirementAge: parseInt(e.target.value)
                      })}
                      className="input-field"
                      min="50"
                      max="80"
                    />
                  </div>
                )}
              </div>
              
              <div className="flex space-x-3">
                <button onClick={handleAddMember} className="btn-primary">
                  Add Member
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false)
                    setNewMember({
                      name: '',
                      dateOfBirth: '',
                      relationship: 'spouse',
                      retirementAge: 65,
                    })
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {currentPlan.familyMembers.filter(m => m.relationship !== 'client').length === 0 && !showAddForm && (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-2 opacity-30" />
            <p>No family members added yet</p>
            <p className="text-sm mt-1">Add spouse, children, or other dependents</p>
          </div>
        )}
      </div>
    </div>
  )
}
